import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Table, Empty, Tag, InputNumber, Select, Tooltip, Input } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faX } from "@fortawesome/free-solid-svg-icons";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { updateAction, updateFormAction } from "../../../redux/actions/actionsSlice";
import { usePostActionMutation, usePutActionMutation } from "../../../redux/actions/actionsAPI";
// COMPONENTS
import TableNoDataBox from "../../components/table/TableNoDataBox";
import ActionsTableActions from "./ActionsTableActions";
import ActionsHeader from "./ActionsHeader";
import GlobalAlert2 from "../../home/GlobalAlert2";
// AUTHORIZATION
import validatePermission from "../../../utils/validatePermissions";
import GETJwtToken from "../../../redux/authentication/authentication";
// UTILS
import { getTableRowClass } from "../../../utils/utils";
// INTERFACES
import { RootState } from "../../../app/store";
import { ActionInterface } from "../../../interfaces/Actions.interface";
// CUSTOM HOOKS
import useGetAllActions from "../../../customHooks/GET_requests/useGetAllActions";
// CONSTANTS
import { BLANK_ACTION, ELEVATOR_CONTROLS } from "../../../constants/action";

function ActionsTable() {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const dispatch = useDispatch();
	const [t] = useTranslation("global");
	const { Column, ColumnGroup } = Table;
	const { Option } = Select;

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [action, setAction] = useState<ActionInterface>({ ...BLANK_ACTION });
	const [pagination, setPagination] = useState<any>({
		current: 1,
		pageSize: 10,
		showSizeChanger: true,
		pageSizeOptions: ["10", "20", "50", "100"],
	});

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [
		triggerPostAction,
		{
			isSuccess: isSuccessPostAction,
			isLoading: isLoadingPostAction,
			isError: isErrorPostAction,
			error: errorPostAction,
			reset: resetPostAction,
		},
	] = usePostActionMutation();

	const [
		triggerPutAction,
		{
			isSuccess: isSuccessPutAction,
			isLoading: isLoadingPutAction,
			isError: isErrorPutAction,
			error: errorPutAction,
			reset: resetPutAction,
		},
	] = usePutActionMutation();

	// ************************************************ */
	// CUSTOM HOOKS *********************************** */
	const { actions, isLoadingGetAllActions, errorMessageGetAllActions, isErrorGetAllActions, totalRowsGetAllActions } =
		useGetAllActions({ page: pagination.current, limit: pagination.pageSize });

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { theme } = useSelector((state: RootState) => state.home);
	const { permissions } = useSelector((state: RootState) => state.user);
	const { formAction, action: selectedAction } = useSelector((state: RootState) => state.action);

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const onChangeFormValue = (newValue: string | number, variableName: string) => {
		const copy: ActionInterface = structuredClone(action);
		if (variableName === "message_enum" && typeof newValue === "number") {
			copy.message_enum = newValue;
		} else if (variableName === "target_position" && typeof newValue === "number") {
			copy.target_position = newValue;
		} else if (variableName === "lift_type" && typeof newValue === "string") {
			copy.lift_type = newValue;
		} else if (variableName === "label" && typeof newValue === "string") {
			copy.label = newValue;
		} else if (variableName === "tooltip" && typeof newValue === "string") {
			copy.tooltip = newValue;
		}
		setAction(copy);
	};

	const onClickSave = async () => {
		const token = await GETJwtToken();
		if (formAction === "create") {
			const BODY = {
				token,
				body: { ...action, valid_states: [] },
			};
			triggerPostAction(BODY);
		} else {
			const copy = structuredClone(action);
			const BODY = {
				token,
				id: action.id,
				body: copy,
			};
			triggerPutAction(BODY);
		}
	};

	const resetForm = () => {
		resetPostAction();
		resetPutAction();
		setAction({ ...BLANK_ACTION });
		dispatch(updateFormAction("create"));
		dispatch(updateAction({ ...BLANK_ACTION }));
	};

	const checkFormIsValid = () => {
		let formIsValid = true;
		if (action.target_position < 0 || action.lift_type === "" || action.message_enum < 0) {
			formIsValid = false;
		}
		return formIsValid;
	};

	const handleTableChange = async (pagination_: any) => {
		setPagination({
			...pagination,
			current: pagination_.current,
			pageSize: pagination_.pageSize,
			total: pagination_.total,
		});
	};

	// eslint-disable-next-line arrow-body-style
	const formatCamelCaseLabel = (value: string): string => {
		return value.replace(/([a-z])([A-Z])/g, "$1 $2");
	};

	const getElevatorControlNameById = (id: number): string => {
		const control = ELEVATOR_CONTROLS.find((item) => item.id === id);
		if (!control?.name) return "Unknown";

		return formatCamelCaseLabel(control.name);
	};

	const onChangeValidStates = (newValue: string[]) => {
		const copy: ActionInterface = structuredClone(action);
		copy.valid_states = newValue;
		setAction(copy);
	};

	// ************************************************* */
	// USE EFFECT ************************************** */
	useEffect(() => {
		resetForm();
	}, [isSuccessPostAction, isSuccessPutAction, isErrorPostAction, isErrorPutAction]);

	useEffect(() => {
		if (formAction === "create") {
			setAction({ ...BLANK_ACTION });
		}
	}, [formAction]);

	useEffect(() => {
		setAction(selectedAction);
	}, [selectedAction]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<div>
			<div>
				{validatePermission("permission-not-defined", permissions) ? (
					<>
						<ActionsHeader />
						<Table
							locale={{
								emptyText: (
									<TableNoDataBox
										isError={isErrorGetAllActions}
										errorMessage={errorMessageGetAllActions}
										noItemsFound='No actions found'
									/>
								),
							}}
							rowClassName={(record, index) => getTableRowClass(index, theme)}
							dataSource={actions}
							pagination={{ ...pagination, total: totalRowsGetAllActions }}
							onChange={handleTableChange}
							size='small'
							className='mt-3'
							loading={isLoadingGetAllActions || isLoadingPostAction || isLoadingPutAction}
							footer={() => (
								<div className='generalStyles__flexEnd'>
									<Tag>{totalRowsGetAllActions} Rows</Tag>
								</div>
							)}
						>
							{/** LIFT TYPE */}
							<ColumnGroup title='Lift Type'>
								<Column
									title={
										<Select
											showSearch
											optionFilterProp='children'
											className='generalStyles__width100'
											allowClear={false}
											size='small'
											value={formAction === "create" ? action.lift_type : ""}
											onChange={(e) => {
												onChangeFormValue(e, "lift_type");
											}}
										>
											{["ELEVATOR", "ESCALATOR"].map((item: string) => (
												<Option key={item} value={item}>
													{item}
												</Option>
											))}
										</Select>
									}
									dataIndex='lift_type'
									key='lift_type'
									render={(text, record: ActionInterface) => (
										<div>
											{formAction === "edit" && record.id === action.id ? (
												<Select
													showSearch
													optionFilterProp='children'
													className='generalStyles__width100'
													allowClear={false}
													size='small'
													value={action.lift_type}
													onChange={(e) => {
														onChangeFormValue(e, "lift_type");
													}}
												>
													{["ELEVATOR", "ESCALATOR"].map((item: string) => (
														<Option key={item} value={item}>
															{item}
														</Option>
													))}
												</Select>
											) : (
												<span>{text || "--"}</span>
											)}
										</div>
									)}
								/>
							</ColumnGroup>
							{/** MESSAGE */}
							<ColumnGroup title='Message'>
								<Column
									title={
										<Select
											showSearch
											optionFilterProp='children'
											className='generalStyles__width100'
											allowClear={false}
											size='small'
											value={formAction === "create" ? action.message_enum : ""}
											onChange={(e) => {
												onChangeFormValue(e, "message_enum");
											}}
										>
											{ELEVATOR_CONTROLS.map((item: { name: string; id: number }) => (
												<Option key={item.id} value={item.id}>
													{item.name}
												</Option>
											))}
										</Select>
									}
									dataIndex='message_enum'
									key='message_enum'
									render={(text, record: ActionInterface) => (
										<div>
											{formAction === "edit" && record.id === action.id ? (
												<Select
													showSearch
													optionFilterProp='children'
													className='generalStyles__width100'
													allowClear={false}
													size='small'
													value={action.message_enum}
													onChange={(e) => {
														onChangeFormValue(e, "message_enum");
													}}
												>
													{ELEVATOR_CONTROLS.map((item: { name: string; id: number }) => (
														<Option key={item.id} value={item.id}>
															{item.name}
														</Option>
													))}
												</Select>
											) : (
												<span>{getElevatorControlNameById(text) || "--"}</span>
											)}
										</div>
									)}
								/>
							</ColumnGroup>
							{/** POSITION */}
							<ColumnGroup title='Position'>
								<Column
									title={
										<InputNumber
											className='generalStyles__input'
											size='small'
											style={{ width: "100%" }}
											min={0}
											value={action.target_position}
											onChange={(e: any) => {
												onChangeFormValue(e, "target_position");
											}}
										/>
									}
									dataIndex='target_position'
									key='target_position'
									render={(text, record: ActionInterface) => (
										<div>
											{formAction === "edit" && record.id === action.id ? (
												<InputNumber
													className='generalStyles__input'
													size='small'
													style={{ width: "100%" }}
													min={0}
													value={action.target_position}
													onChange={(e: any) => {
														onChangeFormValue(e, "target_position");
													}}
												/>
											) : (
												<span>{text || "--"}</span>
											)}
										</div>
									)}
								/>
							</ColumnGroup>
							{/** VALID STATES */}
							<ColumnGroup title='Valid States'>
								<Column
									title={
										<Select
											size='small'
											mode='tags'
											style={{ width: "100%" }}
											placeholder='Tags Mode'
											value={formAction === "create" ? action.valid_states : []}
											disabled={formAction === "edit"}
											onChange={onChangeValidStates}
											options={[]}
										/>
									}
									dataIndex='validStates'
									key='validStates'
									render={(text: any, record: ActionInterface) => (
										<div>
											{formAction === "edit" && record.id === action.id ? (
												<Select
													size='small'
													mode='tags'
													style={{ width: "100%" }}
													placeholder='Tags Mode'
													value={action.valid_states}
													onChange={onChangeValidStates}
													options={[]}
												/>
											) : (
												<div>
													{record.valid_states.map((item: string) => (
														<Tag color='magenta'>{item}</Tag>
													))}
												</div>
											)}
										</div>
									)}
								/>
							</ColumnGroup>
							{/** LABEL */}
							<ColumnGroup title='Label'>
								<Column
									title={
										<Input
											size='small'
											placeholder='Label'
											value={formAction === "create" ? action.label : ""}
											disabled={formAction === "edit"}
											onChange={(event: React.FormEvent<HTMLInputElement>) => {
												onChangeFormValue(event.currentTarget.value, "label");
											}}
										/>
									}
									dataIndex='label'
									key='label'
									render={(text, record: ActionInterface) => (
										<div>
											{formAction === "edit" && record.id === action.id ? (
												<Input
													size='small'
													placeholder='Label'
													value={action.label}
													onChange={(event: React.FormEvent<HTMLInputElement>) => {
														onChangeFormValue(event.currentTarget.value, "label");
													}}
												/>
											) : (
												<span>{text || "--"}</span>
											)}
										</div>
									)}
								/>
							</ColumnGroup>
							{/** TOOLTIP */}
							<ColumnGroup title='Tooltip'>
								<Column
									title={
										<Input
											size='small'
											placeholder='Tooltip'
											value={formAction === "create" ? action.tooltip : ""}
											disabled={formAction === "edit"}
											onChange={(event: React.FormEvent<HTMLInputElement>) => {
												onChangeFormValue(event.currentTarget.value, "tooltip");
											}}
										/>
									}
									dataIndex='tooltip'
									key='tooltip'
									render={(text, record: ActionInterface) => (
										<div>
											{formAction === "edit" && record.id === action.id ? (
												<Input
													size='small'
													placeholder='Tooltip'
													value={action.tooltip}
													onChange={(event: React.FormEvent<HTMLInputElement>) => {
														onChangeFormValue(event.currentTarget.value, "tooltip");
													}}
												/>
											) : (
												<span>{text || "--"}</span>
											)}
										</div>
									)}
								/>
							</ColumnGroup>
							{/** ACTIONS */}
							<ColumnGroup align='center' title={t("edgeManagement.banks.actions")}>
								<Column
									align='center'
									title={
										<div className='generalStyles__flexCenter'>
											<div>
												<Tooltip title={t("general.save")}>
													<Button
														className={formAction === "create" && checkFormIsValid() ? "buttonStyle__10" : ""}
														disabled={!checkFormIsValid() || formAction === "edit"}
														onClick={onClickSave}
														icon={<FontAwesomeIcon icon={faSave} />}
													/>
												</Tooltip>
											</div>
											<div className='generalStyles__mlFix'>
												<Tooltip title={t("general.clear")}>
													<Button
														className={formAction === "create" ? "buttonStyle__14" : ""}
														onClick={resetForm}
														disabled={formAction === "edit"}
														icon={<FontAwesomeIcon icon={faX} />}
													/>
												</Tooltip>
											</div>
										</div>
									}
									key='action'
									render={(_: any, record: ActionInterface) => (
										<div>
											{formAction === "edit" && record.id === action.id ? (
												<div className='generalStyles__flexCenter'>
													<div>
														<Tooltip title={t("general.save")}>
															<Button
																className='buttonStyle__10'
																disabled={!checkFormIsValid()}
																onClick={onClickSave}
																icon={<FontAwesomeIcon icon={faSave} />}
															/>
														</Tooltip>
													</div>
													<div className='generalStyles__mlFix'>
														<Tooltip title={t("general.cancel")}>
															<Button
																className='buttonStyle__14'
																onClick={resetForm}
																icon={<FontAwesomeIcon icon={faX} />}
															/>
														</Tooltip>
													</div>
												</div>
											) : (
												<ActionsTableActions selectedItem={record} />
											)}
										</div>
									)}
								/>
							</ColumnGroup>
						</Table>
					</>
				) : (
					<>
						<div className='generalStyles__noAccessToListTable'>
							<Empty
								description={t("organizationManagement.listNotAvailableOrganizations")}
								image={Empty.PRESENTED_IMAGE_SIMPLE}
							/>
						</div>
					</>
				)}
			</div>
			{/** ---------------------------------------------------- */}
			{/** GLOBAL ALERT */}
			<GlobalAlert2
				isError={isErrorPostAction}
				isSuccess={isSuccessPostAction}
				requestType='POST'
				error={errorPostAction}
				name='Action'
			/>
			<GlobalAlert2
				isError={isErrorPutAction}
				isSuccess={isSuccessPutAction}
				requestType='PUT'
				error={errorPutAction}
				name='Action'
			/>
		</div>
	);
}

export default ActionsTable;
